import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ToastController } from '@ionic/angular';
import { finalize } from 'rxjs';

import { GroupManagementService } from '@services/group-management.service';
import { PanelHostService } from '@services/panel-host.service';
import { rosterParticipantFromPanelContext } from '@services/panel-store';
import { TripService } from '@services/trip.service';
import { RosterParticipant } from '../../../models';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';

@Component({
  selector: 'app-send-message',
  imports: [FormsModule, TranslocoDirective, ContentContainerComponent, PanelBodyComponent],
  templateUrl: './send-message.component.html',
  styleUrls: ['./send-message.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SendMessageComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripService = inject(TripService);
  private readonly groupManagementService = inject(GroupManagementService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);

  readonly submitting = signal(false);
  readonly messageBody = signal('');

  readonly participants = computed((): RosterParticipant[] => {
    const context = this.panelHost.rosterPanelContext();
    if (context?.participants?.length) {
      return context.participants;
    }
    const participant = rosterParticipantFromPanelContext(context);
    if (participant) {
      return [participant];
    }
    return [];
  });

  readonly isBulk = computed(() => this.participants().length > 1);

  constructor() {
    effect(() => {
      if (this.panelHost.activePanel() === 'send-message') {
        this.messageBody.set('');
        this.submitting.set(false);
      }
    });
  }

  cancel(): void {
    this.panelHost.dismiss();
  }

  confirm(): void {
    const participants = this.participants();
    const trip = this.tripService.currentTrip.value();
    const messageBody = this.messageBody().trim();
    if (participants.length === 0 || !trip || !messageBody || this.submitting()) {
      return;
    }

    this.submitting.set(true);
    this.groupManagementService
      .sendParticipantMessages(trip.id, participants.map((p) => p.id), messageBody)
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: async (response) => {
          if (!response.success) {
            const toast = await this.toastController.create({
              message: this.transloco.translate(
                this.isBulk()
                  ? marker('group-management.panels.send-message.error-bulk')
                  : marker('group-management.panels.send-message.error'),
                this.isBulk()
                  ? { count: participants.length }
                  : { name: participants[0].firstname },
              ),
              duration: 4000,
              color: 'danger',
            });
            await toast.present();
            return;
          }

          const toast = await this.toastController.create({
            message: this.transloco.translate(
              this.isBulk()
                ? marker('group-management.panels.send-message.success-bulk')
                : marker('group-management.panels.send-message.success'),
              this.isBulk()
                ? { count: participants.length }
                : { name: participants[0].firstname },
            ),
            duration: 4000,
            color: 'success',
          });
          await toast.present();
          this.panelHost.dismiss();
        },
        error: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(
              this.isBulk()
                ? marker('group-management.panels.send-message.error-bulk')
                : marker('group-management.panels.send-message.error'),
              this.isBulk()
                ? { count: participants.length }
                : { name: participants[0].firstname },
            ),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }
}
